import { useMemo, useState } from "react";
import {
  BedDouble, Percent, Users, Globe, CreditCard, Building2, Smartphone,
  Car, Hourglass, XCircle, ClipboardList, Waves, Coffee, Sparkles, Timer,
  UserX, CalendarX, ChevronLeft, ChevronRight, LayoutGrid, CalendarDays,
  Tag, RefreshCw, Database, Info, MapPin,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RTooltip, Legend as RLegend,
  LineChart, Line, CartesianGrid, XAxis, YAxis, BarChart, Bar,
} from "recharts";

/* ============================================================
   DATA CONTRACT
   ------------------------------------------------------------
   Every field below is what the API is expected to fill in.
   Nothing here is hard-coded into the render path — when a
   field is null/undefined/empty the UI falls back to a quiet
   "—" / skeleton state instead of guessing a number.
   Flip the "Sample data" switch in the toolbar to preview the
   populated look; turn it off to see the real waiting-for-API
   state your integration will start from.
============================================================ */

const emptyViewRoom = {
  branchName: undefined,
  room: undefined,
  roomTotal: undefined,
  capacity: undefined,
  femaleTotal: undefined,
  OTATotal: undefined,
  OTATotalDetails: {},
  memberShipTotal: undefined,
  companyTotal: undefined,
  fromAzWebTotal: undefined,
  fromAzAppTotal: undefined,
  pickUpTotal: undefined,
  dropOffTotal: undefined,
  waitingListTotal: undefined,
  cancelListTotal: undefined,
  surveyTotal: undefined,
  totalRoten: undefined,
  totalBF: undefined,
  totalMassage: undefined,
  totalShortTime: undefined,
  totalNoshow: undefined,
  totalCancelRoom: undefined,
  dataCapacityMonth: [],
  dataCapacityMonthArea: [],
  content: [],
};

const sampleViewRoom = {
  branchName: "Azumaya Ha Noi",
  room: 40,
  roomTotal: 1180,
  capacity: 82.4,
  femaleTotal: 512,
  OTATotal: 640,
  OTATotalDetails: { agoda: 260, "booking.com": 210, "trip.com": 170 },
  memberShipTotal: 145,
  companyTotal: 96,
  fromAzWebTotal: 88,
  fromAzAppTotal: 132,
  pickUpTotal: 58,
  dropOffTotal: 61,
  waitingListTotal: 14,
  cancelListTotal: 9,
  surveyTotal: 203,
  totalRoten: 76,
  totalBF: 940,
  totalMassage: 44,
  totalShortTime: 27,
  totalNoshow: 12,
  totalCancelRoom: 8,
  dataCapacityMonth: [55, 62, 68, 72, 78, 85, 90, 88, 83, 75, 68, 74],
  dataCapacityMonthArea: [
    { month: "T1", TVL1: 55, TVL2: 52, LTT: 40 },
    { month: "T2", TVL1: 60, TVL2: 58, LTT: 45 },
    { month: "T3", TVL1: 62, TVL2: 60, LTT: 48 },
    { month: "T4", TVL1: 68, TVL2: 65, LTT: 60 },
    { month: "T5", TVL1: 70, TVL2: 69, LTT: 72 },
    { month: "T6", TVL1: 75, TVL2: 73, LTT: 78 },
    { month: "T7", TVL1: 80, TVL2: 78, LTT: 82 },
    { month: "T8", TVL1: 78, TVL2: 76, LTT: 74 },
    { month: "T9", TVL1: 74, TVL2: 72, LTT: 65 },
    { month: "T10", TVL1: 70, TVL2: 68, LTT: 58 },
    { month: "T11", TVL1: 65, TVL2: 63, LTT: 50 },
    { month: "T12", TVL1: 60, TVL2: 58, LTT: 55 },
  ],
  content: [], // filled per-month by generateDemoMonth()
};

const AREAS = ["Ha Noi", "Ho Chi Minh", "Da Nang"];
const BRANCHES = {
  "Ha Noi": ["Azumaya Ha Noi", "Azumaya Tay Ho"],
  "Ho Chi Minh": ["Azumaya Bui Vien", "Azumaya Le Van Sy"],
  "Da Nang": ["Azumaya Da Nang"],
};

const OTA_COLORS = { "agoda": "#D8952E", "booking.com": "#146B70", "trip.com": "#7C6FE0", "expedia": "#E5484D", "rakuten": "#2FA36B", "hafh": "#3E7CB1" };

const RULES = [
  { min: 90, color: "var(--red)", label: "≥ 90%" },
  { min: 80, color: "var(--orange)", label: "≥ 80%" },
  { min: 70, color: "var(--yellow)", label: "≥ 70%" },
  { min: 60, color: "var(--green)", label: "≥ 60%" },
];

function getRuleColor(percent) {
  if (percent === null || percent === undefined || Number.isNaN(percent)) return "var(--neutral-cell)";
  if (percent >= 90) return "var(--red)";
  if (percent >= 80) return "var(--orange)";
  if (percent >= 70) return "var(--yellow)";
  if (percent >= 60) return "var(--green)";
  return "var(--neutral-cell)";
}

function fmt(v, digits = 0) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  if (typeof v === "number") return v.toLocaleString("en-US", { maximumFractionDigits: digits });
  return v;
}

// deterministic pseudo-random so the demo calendar looks stable across renders
function seededPercent(seed) {
  const x = Math.sin(seed * 999) * 10000;
  const frac = x - Math.floor(x);
  return Math.round(40 + frac * 58);
}

function generateDemoMonth(year, month) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const seed = year * 100 + month * 31 + d;
    const percent = seededPercent(seed);
    days.push({
      date: `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
      percent,
      guestNumber: Math.round((percent / 100) * 40),
      memberShip: Math.round(percent / 12),
      OTA: Math.round(percent / 6),
      late: seed % 5,
      totalPrice: Math.round(percent * 42.5),
    });
  }
  return days;
}

function buildCalendarGrid(year, month, contentByDate) {
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, date: dateStr, data: contentByDate[dateStr] || null });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const KPI_GROUPS = [
  {
    title: "Occupancy",
    items: [
      { key: "roomTotal", label: "Total Room", icon: BedDouble, unit: "nights" },
      { key: "capacity", label: "Capacity", icon: Percent, unit: "%" },
      { key: "femaleTotal", label: "Female Guests", icon: Users, unit: "nights" },
    ],
  },
  {
    title: "Channels",
    items: [
      { key: "OTATotal", label: "OTA", icon: Globe, unit: "nights" },
      { key: "memberShipTotal", label: "Membership", icon: CreditCard, unit: "nights" },
      { key: "companyTotal", label: "Company", icon: Building2, unit: "nights" },
      { key: "fromAzWebTotal", label: "Az Website", icon: Globe, unit: "turns" },
      { key: "fromAzAppTotal", label: "Az App", icon: Smartphone, unit: "turns" },
    ],
  },
  {
    title: "Operations",
    items: [
      { key: "pickUpTotal", label: "Pick Up", icon: Car, unit: "turns" },
      { key: "dropOffTotal", label: "Drop Off", icon: Car, unit: "turns" },
      { key: "waitingListTotal", label: "Waiting Area", icon: Hourglass, unit: "turns" },
      { key: "cancelListTotal", label: "Cancel Area", icon: XCircle, unit: "turns" },
      { key: "surveyTotal", label: "Survey", icon: ClipboardList, unit: "turns" },
      { key: "totalRoten", label: "Roten", icon: Waves, unit: "turns" },
      { key: "totalBF", label: "Breakfast", icon: Coffee, unit: "turns" },
      { key: "totalMassage", label: "Massage", icon: Sparkles, unit: "turns" },
      { key: "totalShortTime", label: "Shorttime", icon: Timer, unit: "turns" },
      { key: "totalNoshow", label: "No Show", icon: UserX, unit: "turns" },
      { key: "totalCancelRoom", label: "Cancel Room", icon: CalendarX, unit: "turns" },
    ],
  },
];

const NAV_ITEMS = [
  { key: "overview", label: "Report Branch", icon: LayoutGrid },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "membership", label: "Membership", icon: CreditCard },
  { key: "voucher", label: "Voucher", icon: Tag },
];

export default function RoomReportDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [demoMode, setDemoMode] = useState(true);

  const [area, setArea] = useState("Ha Noi");
  const [branch, setBranch] = useState("Azumaya Ha Noi");
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(true);

  const [calDate, setCalDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [activeDay, setActiveDay] = useState(null);

  const viewRoom = demoMode && loaded ? sampleViewRoom : emptyViewRoom;

  const contentByDate = useMemo(() => {
    if (!demoMode) return {};
    const days = generateDemoMonth(calDate.getFullYear(), calDate.getMonth());
    return Object.fromEntries(days.map((d) => [d.date, d]));
  }, [demoMode, calDate]);

  const calendarCells = useMemo(
    () => buildCalendarGrid(calDate.getFullYear(), calDate.getMonth(), contentByDate),
    [calDate, contentByDate]
  );

  const branchesForArea = BRANCHES[area] || [];

  function handleLoadData() {
    setLoading(true);
    setLoaded(false);
    setTimeout(() => {
      setLoading(false);
      setLoaded(true);
    }, 650);
  }

  function shiftMonth(delta) {
    setCalDate((d) => new Date(d.getFullYear(), d.getMonth() + delta, 1));
    setActiveDay(null);
  }

  const otaEntries = Object.entries(viewRoom.OTATotalDetails || {});
  const otaHasData = otaEntries.length > 0;

  return (
    <div className="rrt-app">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap');

        .rrt-app {
          --ink: #12172A;
          --ink-soft: #232A42;
          --paper: #F4F5F9;
          --card: #FFFFFF;
          --border: #E4E7EF;
          --accent: #D8952E;
          --accent-soft: #FBE9D0;
          --accent-2: #146B70;
          --accent-2-soft: #DCEEEE;
          --text: #1B2033;
          --text-soft: #6C7188;
          --red: #E5484D;
          --orange: #F0983C;
          --yellow: #E7C34C;
          --green: #2FA36B;
          --neutral-cell: #EEF0F5;
          font-family: 'Inter', sans-serif;
          color: var(--text);
          background: var(--paper);
          min-height: 100%;
          display: flex;
          border-radius: 14px;
          overflow: hidden;
          border: 1px solid var(--border);
        }
        .rrt-app * { box-sizing: border-box; }
        .rrt-mono { font-family: 'IBM Plex Mono', monospace; font-variant-numeric: tabular-nums; }
        .rrt-display { font-family: 'Sora', sans-serif; }

        /* ---------- Sidebar ---------- */
        .rrt-sidebar {
          width: 216px;
          flex-shrink: 0;
          background: var(--ink);
          color: #C7CBDE;
          padding: 22px 14px;
          display: flex;
          flex-direction: column;
          gap: 22px;
        }
        .rrt-brand {
          display: flex;
          align-items: baseline;
          gap: 8px;
          padding: 0 8px;
        }
        .rrt-brand-mark {
          width: 10px; height: 10px; border-radius: 3px;
          background: var(--accent);
          flex-shrink: 0;
        }
        .rrt-brand-text { font-size: 0.95rem; font-weight: 700; color: #fff; letter-spacing: 0.02em; }
        .rrt-brand-sub { font-size: 0.68rem; color: #7D839C; margin-top: 2px; padding-left: 8px; letter-spacing: 0.04em; text-transform: uppercase; }

        .rrt-nav { display: flex; flex-direction: column; gap: 3px; margin-top: 4px; }
        .rrt-nav-item {
          display: flex; align-items: center; gap: 10px;
          padding: 9px 10px;
          border-radius: 8px;
          font-size: 0.86rem;
          font-weight: 500;
          color: #A9AEC4;
          cursor: pointer;
          border: none;
          background: transparent;
          text-align: left;
          transition: background .15s, color .15s;
        }
        .rrt-nav-item:hover { background: #1B2138; color: #fff; }
        .rrt-nav-item.active { background: var(--accent); color: #201404; font-weight: 600; }
        .rrt-nav-item svg { flex-shrink: 0; }

        .rrt-sidebar-foot {
          margin-top: auto;
          padding: 12px 10px;
          border-top: 1px solid #232A42;
          font-size: 0.72rem;
          color: #6C7188;
          line-height: 1.5;
        }

        /* ---------- Main ---------- */
        .rrt-main { flex: 1; min-width: 0; display: flex; flex-direction: column; max-height: 92vh; overflow-y: auto; }

        .rrt-toolbar {
          position: sticky; top: 0; z-index: 5;
          background: var(--card);
          border-bottom: 1px solid var(--border);
          padding: 14px 24px;
          display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
        }
        .rrt-toolbar-title { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 1.05rem; margin-right: 8px; white-space: nowrap; }
        .rrt-field { display: flex; flex-direction: column; gap: 3px; }
        .rrt-field label { font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-soft); font-weight: 600; }
        .rrt-select, .rrt-month {
          border: 1px solid var(--border);
          background: var(--paper);
          border-radius: 7px;
          padding: 7px 10px;
          font-size: 0.83rem;
          color: var(--text);
          font-family: 'Inter', sans-serif;
        }
        .rrt-btn {
          display: inline-flex; align-items: center; gap: 6px;
          border: 1px solid var(--border);
          background: var(--card);
          padding: 8px 14px;
          border-radius: 7px;
          font-size: 0.83rem;
          font-weight: 600;
          cursor: pointer;
          color: var(--text);
        }
        .rrt-btn-primary { background: var(--ink); color: #fff; border-color: var(--ink); }
        .rrt-btn-primary:hover { background: var(--ink-soft); }
        .rrt-btn:disabled { opacity: 0.55; cursor: not-allowed; }

        .rrt-switch-wrap { margin-left: auto; display: flex; align-items: center; gap: 8px; }
        .rrt-switch-label { font-size: 0.78rem; color: var(--text-soft); font-weight: 500; }
        .rrt-switch { position: relative; width: 38px; height: 21px; border-radius: 999px; background: #D7DAE4; border: none; cursor: pointer; transition: background .15s; flex-shrink: 0; }
        .rrt-switch.on { background: var(--accent); }
        .rrt-switch-knob { position: absolute; top: 2px; left: 2px; width: 17px; height: 17px; border-radius: 50%; background: #fff; transition: transform .15s; box-shadow: 0 1px 2px rgba(0,0,0,0.25); }
        .rrt-switch.on .rrt-switch-knob { transform: translateX(17px); }

        .rrt-body { padding: 22px 24px 40px; }

        .rrt-hero {
          display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px;
          background: var(--ink);
          border-radius: 14px;
          padding: 20px 24px;
          color: #fff;
          margin-bottom: 22px;
        }
        .rrt-hero-left { display: flex; align-items: center; gap: 16px; }
        .rrt-hero-ring { position: relative; width: 78px; height: 78px; flex-shrink: 0; }
        .rrt-hero-ring-value { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 1.05rem; font-weight: 700; }
        .rrt-hero-branch { font-family: 'Sora', sans-serif; font-size: 1.2rem; font-weight: 700; }
        .rrt-hero-meta { font-size: 0.8rem; color: #9096B0; margin-top: 3px; display: flex; align-items: center; gap: 6px; }
        .rrt-hero-legend { display: flex; gap: 10px; flex-wrap: wrap; }
        .rrt-chip { display: flex; align-items: center; gap: 6px; font-size: 0.72rem; color: #C7CBDE; background: #1B2138; padding: 5px 10px; border-radius: 999px; }
        .rrt-chip-dot { width: 8px; height: 8px; border-radius: 50%; }

        .rrt-section-title { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 0.88rem; letter-spacing: 0.02em; margin: 26px 0 12px; color: var(--ink); display:flex; align-items:center; gap:8px;}
        .rrt-section-title:first-child { margin-top: 0; }
        .rrt-section-title .count { font-family:'IBM Plex Mono', monospace; font-size:0.68rem; color: var(--text-soft); font-weight: 500; }

        .rrt-kpi-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 12px; }
        .rrt-kpi-card {
          background: var(--card);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 14px;
          display: flex; flex-direction: column; gap: 10px;
        }
        .rrt-kpi-icon { width: 30px; height: 30px; border-radius: 8px; background: var(--accent-soft); color: var(--accent); display: flex; align-items: center; justify-content: center; }
        .rrt-kpi-value { font-family: 'IBM Plex Mono', monospace; font-size: 1.35rem; font-weight: 600; color: var(--ink); }
        .rrt-kpi-unit { font-size: 0.68rem; color: var(--text-soft); font-weight: 500; margin-left: 4px; }
        .rrt-kpi-label { font-size: 0.76rem; color: var(--text-soft); font-weight: 500; }

        .rrt-charts-grid { display: grid; grid-template-columns: 1.1fr 1fr; gap: 14px; }
        @media (max-width: 900px) { .rrt-charts-grid { grid-template-columns: 1fr; } }
        .rrt-chart-card { background: var(--card); border: 1px solid var(--border); border-radius: 12px; padding: 16px; }
        .rrt-chart-title { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 0.85rem; margin-bottom: 10px; display: flex; justify-content: space-between; align-items: baseline; }
        .rrt-chart-title span.sub { font-family: 'IBM Plex Mono', monospace; font-weight: 500; font-size: 0.72rem; color: var(--text-soft); }
        .rrt-empty-note { font-size: 0.78rem; color: var(--text-soft); display: flex; align-items: center; gap: 6px; padding: 30px 10px; justify-content: center; text-align: center; }

        /* ---------- Calendar ---------- */
        .rrt-cal-card { background: var(--card); border: 1px solid var(--border); border-radius: 12px; padding: 18px; }
        .rrt-cal-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
        .rrt-cal-nav { display: flex; align-items: center; gap: 10px; }
        .rrt-cal-navbtn { border: 1px solid var(--border); background: var(--paper); width: 30px; height: 30px; border-radius: 7px; display: flex; align-items: center; justify-content: center; cursor: pointer; }
        .rrt-cal-title { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 1rem; }
        .rrt-cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 6px; }
        .rrt-cal-dayname { text-align: center; font-size: 0.7rem; font-weight: 700; color: var(--text-soft); text-transform: uppercase; letter-spacing: 0.04em; padding-bottom: 4px; }
        .rrt-cal-dayname.weekend { color: var(--red); }
        .rrt-day {
          aspect-ratio: 1 / 0.85;
          border-radius: 9px;
          border: 1.5px solid transparent;
          padding: 7px 8px;
          cursor: pointer;
          display: flex; flex-direction: column; justify-content: space-between;
          transition: transform .1s, border-color .1s;
        }
        .rrt-day:hover { border-color: var(--ink); }
        .rrt-day.active { border-color: var(--ink); box-shadow: 0 0 0 2px rgba(18,23,42,0.12); }
        .rrt-day.empty { visibility: hidden; }
        .rrt-day-num { font-family: 'IBM Plex Mono', monospace; font-weight: 600; font-size: 0.8rem; }
        .rrt-day-pct { font-family: 'IBM Plex Mono', monospace; font-weight: 700; font-size: 0.95rem; align-self: flex-end; }
        .rrt-day-nodata { font-size: 0.62rem; color: var(--text-soft); align-self: flex-end; }

        .rrt-cal-legend { display: flex; gap: 14px; flex-wrap: wrap; margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--border); }
        .rrt-cal-legend-item { display: flex; align-items: center; gap: 6px; font-size: 0.75rem; color: var(--text-soft); }
        .rrt-cal-legend-box { width: 12px; height: 12px; border-radius: 4px; }

        .rrt-daypanel { background: var(--card); border: 1px solid var(--border); border-radius: 12px; padding: 16px; margin-top: 14px; }
        .rrt-daypanel-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 12px; margin-top: 10px; }
        .rrt-daypanel-stat-label { font-size: 0.7rem; color: var(--text-soft); font-weight: 600; text-transform: uppercase; letter-spacing: .03em; }
        .rrt-daypanel-stat-value { font-family: 'IBM Plex Mono', monospace; font-size: 1.1rem; font-weight: 600; color: var(--ink); margin-top: 3px; }

        .rrt-placeholder-tab { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 80px 20px; color: var(--text-soft); text-align: center; }
        .rrt-placeholder-tab .rrt-display { font-size: 1.05rem; color: var(--ink); }
      `}</style>

      {/* ---------------- Sidebar ---------------- */}
      <aside className="rrt-sidebar">
        <div>
          <div className="rrt-brand">
            <span className="rrt-brand-mark" />
            <span className="rrt-brand-text">Azumaya</span>
          </div>
          <div className="rrt-brand-sub">Room Report</div>
        </div>
        <nav className="rrt-nav">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.key}
              className={"rrt-nav-item" + (activeTab === item.key ? " active" : "")}
              onClick={() => setActiveTab(item.key)}
            >
              <item.icon size={16} />
              {item.label}
            </button>
          ))}
        </nav>
        <div className="rrt-sidebar-foot">
          Số liệu do API cung cấp.<br />
          Trường trống hiển thị “—” cho đến khi dữ liệu được nạp.
        </div>
      </aside>

      {/* ---------------- Main ---------------- */}
      <main className="rrt-main">
        <div className="rrt-toolbar">
          <span className="rrt-toolbar-title">
            {activeTab === "overview" ? "Report Branch" : activeTab === "calendar" ? "Occupancy Calendar" : NAV_ITEMS.find(n => n.key === activeTab)?.label}
          </span>

          {(activeTab === "overview") && (
            <>
              <div className="rrt-field">
                <label>Area</label>
                <select className="rrt-select" value={area} onChange={(e) => { setArea(e.target.value); setBranch(BRANCHES[e.target.value]?.[0] || ""); }}>
                  {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div className="rrt-field">
                <label>Branch</label>
                <select className="rrt-select" value={branch} onChange={(e) => setBranch(e.target.value)}>
                  {branchesForArea.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div className="rrt-field">
                <label>Month</label>
                <input className="rrt-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
              </div>
              <div className="rrt-field">
                <label>&nbsp;</label>
                <button className="rrt-btn rrt-btn-primary" onClick={handleLoadData} disabled={loading}>
                  <RefreshCw size={14} className={loading ? "rrt-spin" : ""} />
                  {loading ? "Loading…" : "Load Data"}
                </button>
              </div>
            </>
          )}

          <div className="rrt-switch-wrap">
            <Database size={14} color="var(--text-soft)" />
            <span className="rrt-switch-label">Sample data</span>
            <button className={"rrt-switch" + (demoMode ? " on" : "")} onClick={() => setDemoMode((v) => !v)}>
              <span className="rrt-switch-knob" />
            </button>
          </div>
        </div>

        <div className="rrt-body">
          {activeTab === "overview" && (
            <OverviewTab viewRoom={viewRoom} branch={branch} month={month} otaEntries={otaEntries} otaHasData={otaHasData} loading={loading} />
          )}

          {activeTab === "calendar" && (
            <CalendarTab
              calDate={calDate}
              calendarCells={calendarCells}
              shiftMonth={shiftMonth}
              activeDay={activeDay}
              setActiveDay={setActiveDay}
              room={demoMode ? 40 : undefined}
              demoMode={demoMode}
            />
          )}

          {(activeTab === "membership" || activeTab === "voucher") && (
            <div className="rrt-placeholder-tab">
              <div className="rrt-display">Chưa kết nối module này</div>
              <div style={{ fontSize: "0.82rem", maxWidth: 340 }}>
                Khu vực này giữ nguyên cấu trúc điều hướng gốc — sẵn sàng để gắn component
                {activeTab === "membership" ? " ReportMemberShipAdmin" : " VoucherMemberShip"} khi cần.
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

/* ============================================================ */

function OverviewTab({ viewRoom, branch, otaEntries, otaHasData }) {
  const capacity = viewRoom.capacity;
  const donutData = [
    { name: "Filled", value: capacity ?? 0 },
    { name: "Empty", value: capacity !== undefined && capacity !== null ? Math.max(0, 100 - capacity) : 100 },
  ];

  const hasTrend = viewRoom.dataCapacityMonth && viewRoom.dataCapacityMonth.length > 0;
  const trendData = hasTrend
    ? viewRoom.dataCapacityMonth.map((v, i) => ({ month: ["T1","T2","T3","T4","T5","T6","T7","T8","T9","T10","T11","T12"][i], capacity: v }))
    : [];

  const hasAreaBar = viewRoom.dataCapacityMonthArea && viewRoom.dataCapacityMonthArea.length > 0;

  return (
    <>
      {/* Hero */}
      <div className="rrt-hero">
        <div className="rrt-hero-left">
          <div className="rrt-hero-ring">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={donutData} dataKey="value" innerRadius={26} outerRadius={38} startAngle={90} endAngle={-270} stroke="none">
                  <Cell fill={getRuleColor(capacity)} />
                  <Cell fill="#2A3150" />
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="rrt-hero-ring-value rrt-mono">{fmt(capacity, 1)}{capacity !== undefined && capacity !== null ? "%" : ""}</div>
          </div>
          <div>
            <div className="rrt-hero-branch">{viewRoom.branchName || branch || "Chọn chi nhánh"}</div>
            <div className="rrt-hero-meta"><MapPin size={12} /> {viewRoom.room ? `${viewRoom.room} phòng` : "Chưa có dữ liệu phòng"}</div>
          </div>
        </div>
        <div className="rrt-hero-legend">
          {RULES.map((r) => (
            <span className="rrt-chip" key={r.label}><span className="rrt-chip-dot" style={{ background: r.color }} />{r.label}</span>
          ))}
          <span className="rrt-chip"><span className="rrt-chip-dot" style={{ background: "var(--neutral-cell)" }} />Chưa có dữ liệu</span>
        </div>
      </div>

      {KPI_GROUPS.map((group) => (
        <div key={group.title}>
          <div className="rrt-section-title">{group.title} <span className="count">{group.items.length} chỉ số</span></div>
          <div className="rrt-kpi-grid">
            {group.items.map((item) => (
              <div className="rrt-kpi-card" key={item.key}>
                <div className="rrt-kpi-icon"><item.icon size={16} /></div>
                <div>
                  <span className="rrt-kpi-value">{fmt(viewRoom[item.key], item.key === "capacity" ? 1 : 0)}</span>
                  <span className="rrt-kpi-unit">{item.unit}</span>
                </div>
                <div className="rrt-kpi-label">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="rrt-section-title">Biểu đồ</div>
      <div className="rrt-charts-grid">
        <div className="rrt-chart-card">
          <div className="rrt-chart-title">OTA breakdown <span className="sub">{fmt(viewRoom.OTATotal)} nights</span></div>
          {otaHasData ? (
            <ResponsiveContainer width="100%" height={230}>
              <PieChart>
                <Pie data={otaEntries.map(([k, v]) => ({ name: k, value: v }))} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {otaEntries.map(([k]) => <Cell key={k} fill={OTA_COLORS[k] || "#9096B0"} />)}
                </Pie>
                <RTooltip />
                <RLegend iconType="circle" wrapperStyle={{ fontSize: "0.75rem" }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="rrt-empty-note"><Info size={14} /> Chưa có dữ liệu OTA — sẽ hiển thị khi API trả về OTATotalDetails.</div>
          )}
        </div>

        <div className="rrt-chart-card">
          <div className="rrt-chart-title">Capacity trend <span className="sub">12 tháng</span></div>
          {hasTrend ? (
            <ResponsiveContainer width="100%" height={230}>
              <LineChart data={trendData} margin={{ left: -20, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--text-soft)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "var(--text-soft)" }} axisLine={false} tickLine={false} width={34} />
                <RTooltip />
                <Line type="monotone" dataKey="capacity" stroke="var(--accent-2)" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="rrt-empty-note"><Info size={14} /> Chưa có dữ liệu xu hướng — chờ dataCapacityMonth từ API.</div>
          )}
        </div>

        <div className="rrt-chart-card" style={{ gridColumn: "1 / -1" }}>
          <div className="rrt-chart-title">Area capacity comparison</div>
          {hasAreaBar ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={viewRoom.dataCapacityMonthArea} margin={{ left: -20, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--text-soft)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "var(--text-soft)" }} axisLine={false} tickLine={false} width={34} />
                <RTooltip />
                <RLegend wrapperStyle={{ fontSize: "0.75rem" }} />
                <Bar dataKey="TVL1" fill="var(--accent-2)" radius={[3, 3, 0, 0]} />
                <Bar dataKey="TVL2" fill="var(--accent)" radius={[3, 3, 0, 0]} />
                <Bar dataKey="LTT" fill="#E7C34C" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="rrt-empty-note"><Info size={14} /> Chưa có dữ liệu so sánh khu vực — chờ dataCapacityMonthArea từ API.</div>
          )}
        </div>
      </div>
    </>
  );
}

/* ============================================================ */

function CalendarTab({ calDate, calendarCells, shiftMonth, activeDay, setActiveDay, room, demoMode }) {
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthLabel = calDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const activeCell = activeDay !== null ? calendarCells[activeDay] : null;

  return (
    <>
      <div className="rrt-cal-card">
        <div className="rrt-cal-header">
          <div className="rrt-cal-nav">
            <button className="rrt-cal-navbtn" onClick={() => shiftMonth(-1)}><ChevronLeft size={16} /></button>
            <div className="rrt-cal-title">{monthLabel}</div>
            <button className="rrt-cal-navbtn" onClick={() => shiftMonth(1)}><ChevronRight size={16} /></button>
          </div>
          {!demoMode && <div className="rrt-empty-note" style={{ padding: 0 }}><Info size={13} /> Đang chờ dữ liệu API</div>}
        </div>

        <div className="rrt-cal-grid">
          {dayNames.map((n) => (
            <div key={n} className={"rrt-cal-dayname" + (n === "Sun" || n === "Sat" ? " weekend" : "")}>{n}</div>
          ))}
          {calendarCells.map((cell, i) => {
            if (!cell) return <div key={i} className="rrt-day empty" />;
            const percent = cell.data?.percent;
            const color = getRuleColor(percent);
            const textColor = color === "var(--neutral-cell)" ? "var(--text-soft)" : (color === "var(--yellow)" ? "#3a2f00" : "#fff");
            return (
              <div
                key={i}
                className={"rrt-day" + (activeDay === i ? " active" : "")}
                style={{ background: color }}
                onMouseEnter={() => setActiveDay(i)}
                onClick={() => setActiveDay(i)}
              >
                <span className="rrt-day-num" style={{ color: textColor }}>{cell.day}</span>
                {percent !== undefined && percent !== null ? (
                  <span className="rrt-day-pct" style={{ color: textColor }}>{percent}%</span>
                ) : (
                  <span className="rrt-day-nodata" style={{ color: textColor }}>—</span>
                )}
              </div>
            );
          })}
        </div>

        <div className="rrt-cal-legend">
          {RULES.map((r) => (
            <span className="rrt-cal-legend-item" key={r.label}><span className="rrt-cal-legend-box" style={{ background: r.color }} />{r.label}</span>
          ))}
          <span className="rrt-cal-legend-item"><span className="rrt-cal-legend-box" style={{ background: "var(--neutral-cell)" }} />Chưa có dữ liệu</span>
        </div>
      </div>

      <div className="rrt-daypanel">
        <div className="rrt-chart-title" style={{ marginBottom: 0 }}>
          {activeCell?.date ? `Chi tiết ngày ${activeCell.date}` : "Di chuột / chạm vào một ngày để xem chi tiết"}
        </div>
        <div className="rrt-daypanel-grid">
          <DayStat label="Occupancy" value={activeCell?.data?.percent} unit="%" />
          <DayStat label="Khách" value={activeCell?.data?.guestNumber} unit={room ? `/ ${room} phòng` : "phòng"} />
          <DayStat label="Membership" value={activeCell?.data?.memberShip} unit="lượt" />
          <DayStat label="OTA" value={activeCell?.data?.OTA} unit="lượt" />
          <DayStat label="Trễ nhận phòng" value={activeCell?.data?.late} unit="lượt" />
          <DayStat label="Doanh thu" value={activeCell?.data?.totalPrice} unit="$" />
        </div>
      </div>
    </>
  );
}

function DayStat({ label, value, unit }) {
  return (
    <div>
      <div className="rrt-daypanel-stat-label">{label}</div>
      <div className="rrt-daypanel-stat-value">{fmt(value)} <span style={{ fontSize: "0.65rem", color: "var(--text-soft)", fontWeight: 500 }}>{unit}</span></div>
    </div>
  );
}
